import React, {
    useCallback,
    useEffect,
} from "react";

import {
    FiAlertCircle,
    FiCheckCircle,
    FiRefreshCw,
    FiXCircle,
} from "react-icons/fi";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    fetchGstReconciliation,

    gstReconciliationSelector,
    gstReconciliationSummarySelector,
    gstReconciliationChecksSelector,

    gstReportLoadingSelector,
    gstReportErrorSelector,
} from "../../../../../../../store/billing/gst/gstReportSlice";


const toNumber = (
    value
) => {
    const number =
        Number(value);

    return Number.isFinite(number)
        ? number
        : 0;
};


const formatCurrency = (
    value
) =>
    new Intl.NumberFormat(
        "en-IN",
        {
            style:
                "currency",

            currency:
                "INR",

            minimumFractionDigits:
                2,
        }
    ).format(
        toNumber(value)
    );


const GstReconciliationReport = ({
    fromDate,
    toDate,
    financialYear,
}) => {
    const dispatch =
        useDispatch();


    const reconciliation =
        useSelector(
            gstReconciliationSelector
        );


    const summary =
        useSelector(
            gstReconciliationSummarySelector
        );


    const checks =
        useSelector(
            gstReconciliationChecksSelector
        );


    const loading =
        useSelector(
            gstReportLoadingSelector
        ) || {};


    const errors =
        useSelector(
            gstReportErrorSelector
        ) || {};


    const loadData =
        useCallback(
            () => {
                dispatch(
                    fetchGstReconciliation({
                        fromDate,
                        toDate,
                        financialYear,
                        tolerance:
                            1,
                    })
                );
            },
            [
                dispatch,
                fromDate,
                toDate,
                financialYear,
            ]
        );


    useEffect(
        () => {
            loadData();
        },
        [
            loadData,
        ]
    );


    return (
        <div className="gst-reconciliation">

            {errors.reconciliation && (

                <div className="gst-dashboard-alert">

                    <FiAlertCircle />

                    {errors.reconciliation}

                </div>

            )}


            <section className="gst-dashboard-summary-grid">

                <div className="gst-dashboard-summary-card">

                    <span>
                        Total Checks
                    </span>

                    <strong>
                        {summary?.totalChecks ||
                            0}
                    </strong>

                    <small>
                        Reconciliation checks
                    </small>

                </div>


                <div className="gst-dashboard-summary-card">

                    <span>
                        Matched
                    </span>

                    <strong>
                        {summary?.matched ||
                            0}
                    </strong>

                    <small>
                        Values matched
                    </small>

                </div>


                <div className="gst-dashboard-summary-card">

                    <span>
                        Mismatched
                    </span>

                    <strong>
                        {summary?.mismatched ||
                            0}
                    </strong>

                    <small>
                        Requires review
                    </small>

                </div>


                <div className="gst-dashboard-summary-card">

                    <span>
                        Filing Status
                    </span>

                    <strong>
                        {summary?.filingReady
                            ? "READY"
                            : "REVIEW"}
                    </strong>

                    <small>
                        Internal validation status
                    </small>

                </div>

            </section>


            <section className="gst-reconciliation-header">

                <div>

                    {summary?.filingReady ? (

                        <FiCheckCircle />

                    ) : (

                        <FiAlertCircle />

                    )}


                    <div>

                        <strong>
                            GST Reconciliation
                        </strong>

                        <span>
                            GSTR-1 vs GSTR-3B vs HSN Summary
                        </span>

                    </div>

                </div>


                <button
                    type="button"
                    className="gst-dashboard-button"
                    onClick={
                        loadData
                    }
                    disabled={
                        loading.reconciliation
                    }
                >

                    <FiRefreshCw
                        className={
                            loading.reconciliation
                                ? "gst-dashboard-spinner"
                                : ""
                        }
                    />

                    Reconcile

                </button>

            </section>


            <section className="gst-reconciliation-table-wrapper">

                <table className="gst-reconciliation-table">

                    <thead>

                        <tr>

                            <th>
                                Check
                            </th>

                            <th>
                                Status
                            </th>

                            <th className="text-right">
                                Source
                            </th>

                            <th className="text-right">
                                Target
                            </th>

                            <th className="text-right">
                                Difference
                            </th>

                        </tr>

                    </thead>


                    <tbody>

                        {checks.map(
                            (
                                check,
                                index
                            ) => (

                                <tr
                                    key={
                                        check?.code ||
                                        index
                                    }
                                >

                                    <td>

                                        <strong>
                                            {check?.label}
                                        </strong>

                                        <small>
                                            {check?.code}
                                        </small>

                                    </td>


                                    <td>

                                        <span
                                            className={
                                                check?.status ===
                                                    "MATCHED"
                                                    ? "gst-recon-status matched"
                                                    : "gst-recon-status mismatch"
                                            }
                                        >

                                            {check?.status ===
                                                "MATCHED" ? (
                                                <FiCheckCircle />
                                            ) : (
                                                <FiXCircle />
                                            )}

                                            {check?.status}

                                        </span>

                                    </td>


                                    {check?.sourceValue !==
                                        undefined ? (

                                        <>

                                            <td className="text-right">

                                                {formatCurrency(
                                                    check
                                                        ?.sourceValue
                                                )}

                                            </td>


                                            <td className="text-right">

                                                {formatCurrency(
                                                    check
                                                        ?.targetValue
                                                )}

                                            </td>


                                            <td className="text-right">

                                                <strong>
                                                    {formatCurrency(
                                                        check
                                                            ?.difference
                                                    )}
                                                </strong>

                                            </td>

                                        </>

                                    ) : (

                                        <td
                                            colSpan="3"
                                            className="text-right"
                                        >

                                            {toNumber(
                                                check?.count
                                            )}{" "}
                                            issue(s)

                                        </td>

                                    )}

                                </tr>

                            )
                        )}

                    </tbody>

                </table>

            </section>


            <section className="gst-reconciliation-values">

                <div>

                    <span>
                        GSTR-1 Tax
                    </span>

                    <strong>
                        {formatCurrency(
                            reconciliation
                                ?.values
                                ?.gstr1
                                ?.totalTax
                        )}
                    </strong>

                </div>


                <div>

                    <span>
                        GSTR-3B Output Tax
                    </span>

                    <strong>
                        {formatCurrency(
                            reconciliation
                                ?.values
                                ?.gstr3b
                                ?.totalTax
                        )}
                    </strong>

                </div>


                <div>

                    <span>
                        HSN Tax
                    </span>

                    <strong>
                        {formatCurrency(
                            reconciliation
                                ?.values
                                ?.hsn
                                ?.totalTax
                        )}
                    </strong>

                </div>

            </section>

        </div>
    );
};


export default GstReconciliationReport;